import { createBinderlessComponentImplementation } from '@a2ui/react/v0_9';
import { Catalog } from '@a2ui/web_core/v0_9';
import { useContext, useMemo } from 'react';
import { z } from 'zod';

import { ChatLayoutContext } from 'components/DeepSearch/context/ChatLayoutContext';

import { DeepSearchContext } from '../context';
import { mapAncestryReportPayload } from '../utils/mapAncestryReportPayload';
import { mapEmailReportPayload } from '../utils/mapEmailReportPayload';
import { mapPersonReportPayload } from '../utils/mapPersonReportPayload';
import { mapPhoneReportPayload } from '../utils/mapPhoneReportPayload';
import { mapPropertyReportPayload } from '../utils/mapPropertyReportPayload';
import { mapUsernameReportPayload } from '../utils/mapUsernameReportPayload';
import { mapVehicleReportPayload } from '../utils/mapVehicleReportPayload';

import {
  AncestryReportPreviewCard,
  AncestrySearchPreviewCard,
  ContactSearchPreviewCard,
  EmailReportPreviewCard,
  ObituaryReportPreviewCard,
  ObituarySearchPreviewCard,
  PersonReportPreviewCard,
  PersonSearchPreviewCard,
  PhoneReportPreviewCard,
  PropertyOptionsPreviewCard,
  PropertyReportPreviewCard,
  SuggestedNextStepsCard,
  UsernameReportPreviewCard,
  VehicleReportPreviewCard,
} from './PreviewCards';

import type {
  AncestrySearchData,
  AncestryTeaserRelative,
  ContactSearchData,
  ObituaryReportData,
  ObituarySearchData,
  PersonSearchData,
  PersonTeaserRelative,
  SuggestedNextStepsData,
} from '../types';

function useViewReport(
  value: Record<string, unknown> | undefined,
  type: string,
  subtype?: string
): (() => void) | undefined {
  const { reportState } = useContext(ChatLayoutContext);
  const permalink =
    value && typeof value.permalink === 'string' && value.permalink.trim()
      ? value.permalink
      : '';
  return permalink
    ? () => reportState.openReportFromReportLibrary(permalink, type, subtype)
    : undefined;
}

export const DEEP_SEARCH_CATALOG_ID = 'deepsearch-report-preview';

const PhoneReportPreview = createBinderlessComponentImplementation(
  {
    name: 'PhoneReportPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const { sendMessage } = useContext(DeepSearchContext);
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;
    const onViewReport = useViewReport(value, 'phone');

    if (!value) {
      return null;
    }

    const data = mapPhoneReportPayload(value);

    return (
      <PhoneReportPreviewCard
        data={data}
        onViewReport={onViewReport}
        onSendMessage={sendMessage}
      />
    );
  }
);

const EmailReportPreview = createBinderlessComponentImplementation(
  {
    name: 'EmailReportPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;
    const onViewReport = useViewReport(value, 'email');
    const { sendMessage } = useContext(DeepSearchContext);

    if (!value) {
      return null;
    }

    const data = mapEmailReportPayload(value);

    return (
      <EmailReportPreviewCard
        data={data}
        onViewReport={onViewReport}
        onSendMessage={sendMessage}
      />
    );
  }
);

const VehicleYmmReportPreview = createBinderlessComponentImplementation(
  {
    name: 'VehicleYmmReportPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;
    const onViewReport = useViewReport(value, 'vehicle', 'ymm');

    if (!value) {
      return null;
    }

    const data = mapVehicleReportPayload(value, 'ymm');

    return <VehicleReportPreviewCard data={data} onViewReport={onViewReport} />;
  }
);

const VehicleVinReportPreview = createBinderlessComponentImplementation(
  {
    name: 'VehicleVinReportPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;
    const onViewReport = useViewReport(value, 'vehicle', 'vin');

    if (!value) {
      return null;
    }

    const data = mapVehicleReportPayload(value, 'vin');

    return <VehicleReportPreviewCard data={data} onViewReport={onViewReport} />;
  }
);

const VehicleLpReportPreview = createBinderlessComponentImplementation(
  {
    name: 'VehicleLpReportPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;
    const onViewReport = useViewReport(value, 'vehicle', 'plate');

    if (!value) {
      return null;
    }

    const data = mapVehicleReportPayload(value, 'plate');

    return <VehicleReportPreviewCard data={data} onViewReport={onViewReport} />;
  }
);

const UsernameReportPreview = createBinderlessComponentImplementation(
  {
    name: 'UsernameReportPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const { sendMessage } = useContext(DeepSearchContext);
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;
    const onViewReport = useViewReport(value, 'username');

    if (!value) {
      return null;
    }

    const data = mapUsernameReportPayload(value);

    function handleViewReportForOwner(ownerName: string, bvId?: string) {
      if (bvId) {
        const displayText = `Full person report on ${ownerName}`;
        sendMessage(`${displayText}. bv_id: ${bvId}`, displayText);
      } else {
        const displayText = `I want to know more about ${ownerName}`;
        sendMessage(displayText, displayText);
      }
    }

    return (
      <UsernameReportPreviewCard
        data={data}
        onViewReport={onViewReport}
        onViewReportForOwner={handleViewReportForOwner}
      />
    );
  }
);

const PropertyReportPreview = createBinderlessComponentImplementation(
  {
    name: 'PropertyReportPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const { sendMessage } = useContext(DeepSearchContext);
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;
    const onViewReport = useViewReport(value, 'property');

    if (!value) {
      return null;
    }

    const data = mapPropertyReportPayload(value);

    return (
      <PropertyReportPreviewCard
        data={data}
        onViewReport={onViewReport}
        onSendMessage={sendMessage}
      />
    );
  }
);

const AncestrySearchPreview = createBinderlessComponentImplementation(
  {
    name: 'AncestrySearchPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const { sendMessage } = useContext(DeepSearchContext);
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;

    if (!value) {
      return null;
    }

    const rawTeasers = Array.isArray(value.teasers) ? value.teasers : [];
    const teasers = rawTeasers
      .filter(
        (t: unknown): t is Record<string, unknown> =>
          typeof t === 'object' && t !== null
      )
      .map((t) => ({
        name: String(t.name ?? ''),
        fsId: String(t.fsId ?? ''),
        birthYear: String(t.birthYear ?? ''),
        deathYear: String(t.deathYear ?? ''),
        location: String(t.location ?? ''),
        relatives: normalizeAncestryRelatives(t.relatives),
      }));

    const data: AncestrySearchData = { teasers };

    function handleTeaserClick(
      displayName: string,
      idType: string,
      idValue: string
    ) {
      const displayText = `full family search report on ${displayName}`;
      sendMessage(`${displayText}. ${idType}: ${idValue}`, displayText);
    }

    return (
      <AncestrySearchPreviewCard
        data={data}
        onTeaserClick={handleTeaserClick}
      />
    );
  }
);

const ObituarySearchPreview = createBinderlessComponentImplementation(
  {
    name: 'ObituarySearchPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const { sendMessage } = useContext(DeepSearchContext);
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;

    if (!value) {
      return null;
    }

    const rawTeasers = Array.isArray(value.teasers) ? value.teasers : [];
    const teasers = rawTeasers
      .filter(
        (t: unknown): t is Record<string, unknown> =>
          typeof t === 'object' && t !== null
      )
      .map((t) => ({
        name: String(t.name ?? ''),
        id: String(t.id ?? ''),
        birthYear: String(t.birthYear ?? ''),
        deathYear: String(t.deathYear ?? ''),
        location: String(t.location ?? ''),
      }));

    const data: ObituarySearchData = { teasers };

    function handleTeaserClick(
      displayName: string,
      idType: string,
      idValue: string
    ) {
      const displayText = `full obituary report on ${displayName}`;
      sendMessage(`${displayText}. ${idType}: ${idValue}`, displayText);
    }

    return (
      <ObituarySearchPreviewCard
        data={data}
        onTeaserClick={handleTeaserClick}
      />
    );
  }
);

const ContactSearchPreview = createBinderlessComponentImplementation(
  {
    name: 'ContactSearchPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const { sendMessage } = useContext(DeepSearchContext);
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;

    if (!value) {
      return null;
    }

    const rawTeasers = Array.isArray(value.teasers) ? value.teasers : [];
    const teasers = rawTeasers
      .filter(
        (t: unknown): t is Record<string, unknown> =>
          typeof t === 'object' && t !== null
      )
      .map((t) => ({
        name: String(t.name ?? ''),
        sourceId: String(t.sourceId ?? ''),
        location: String(t.location ?? ''),
        job: String(t.job ?? ''),
      }));

    const data: ContactSearchData = { teasers };

    function handleTeaserClick(
      displayName: string,
      idType: string,
      idValue: string
    ) {
      const displayText = `full contact report on ${displayName}`;
      sendMessage(`${displayText}. ${idType}: ${idValue}`, displayText);
    }

    return (
      <ContactSearchPreviewCard data={data} onTeaserClick={handleTeaserClick} />
    );
  }
);

const PersonSearchPreview = createBinderlessComponentImplementation(
  {
    name: 'PersonSearchPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const { sendMessage } = useContext(DeepSearchContext);
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;

    if (!value) {
      return null;
    }

    const rawTeasers = Array.isArray(value.teasers) ? value.teasers : [];
    const filtered = rawTeasers.filter(
      (t: unknown): t is Record<string, unknown> =>
        typeof t === 'object' && t !== null
    );

    const isContact = filtered.length > 0 && 'sourceId' in filtered[0];

    if (isContact) {
      const teasers = filtered.map((t) => ({
        name: String(t.name ?? ''),
        sourceId: String(t.sourceId ?? ''),
        location: String(t.location ?? ''),
        job: String(t.job ?? ''),
      }));
      const data: ContactSearchData = { teasers };
      return (
        <ContactSearchPreviewCard
          data={data}
          onTeaserClick={(displayName, idType, idValue) => {
            const displayText = `full contact report on ${displayName}`;
            sendMessage(`${displayText}. ${idType}: ${idValue}`, displayText);
          }}
        />
      );
    }

    const teasers = filtered.map((t) => ({
      name: String(t.name ?? ''),
      bvId: String(t.bvId ?? ''),
      age: String(t.age ?? ''),
      birthYear: String(t.birthYear ?? ''),
      location: String(t.location ?? ''),
      relatives: normalizeRelatives(t.relatives),
    }));

    const data: PersonSearchData = { teasers };

    return (
      <PersonSearchPreviewCard
        data={data}
        onTeaserClick={(displayName, idType, idValue) => {
          const displayText = `full person report on ${displayName}`;
          sendMessage(`${displayText}. ${idType}: ${idValue}`, displayText);
        }}
      />
    );
  }
);

const AncestryReportPreview = createBinderlessComponentImplementation(
  {
    name: 'AncestryReportPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;
    const onViewReport = useViewReport(value, 'ancestor');

    if (!value) {
      return null;
    }

    const data = mapAncestryReportPayload(value);
    return (
      <AncestryReportPreviewCard data={data} onViewReport={onViewReport} />
    );
  }
);

const ObituaryReportPreview = createBinderlessComponentImplementation(
  {
    name: 'ObituaryReportPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;
    const onViewReport = useViewReport(value, 'obituary');

    if (!value) {
      return null;
    }

    const data: ObituaryReportData = {
      name: String(value.name ?? ''),
      dob: String(value.dob ?? ''),
      dod: String(value.dod ?? ''),
      location: String(value.location ?? ''),
      photoUrl: String(value.photoUrl ?? ''),
      obituaryUrl: String(value.obituaryUrl ?? ''),
      funeralHome: String(value.funeralHome ?? ''),
      funeralHomeUrl: String(value.funeralHomeUrl ?? ''),
      funeralHomeAddress: String(value.funeralHomeAddress ?? ''),
      message: String(value.message ?? ''),
    };

    return (
      <ObituaryReportPreviewCard data={data} onViewReport={onViewReport} />
    );
  }
);

const PersonReportPreview = createBinderlessComponentImplementation(
  {
    name: 'PersonReportPreview',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const { sendMessage } = useContext(DeepSearchContext);
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;
    const onViewReport = useViewReport(value, 'person');

    if (!value) {
      return null;
    }

    const data = mapPersonReportPayload(value);

    return (
      <PersonReportPreviewCard
        data={data}
        onViewReport={onViewReport}
        onSendMessage={sendMessage}
      />
    );
  }
);

const PropertyUnitSelector = createBinderlessComponentImplementation(
  {
    name: 'PropertyUnitSelector',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const { sendMessage } = useContext(DeepSearchContext);
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;

    if (!value) {
      return null;
    }

    const options = useMemo(() => {
      const rawOptions = Array.isArray(value.options)
        ? (value.options as Record<string, unknown>[])
        : [];
      return rawOptions
        .filter((o) => typeof o === 'object' && o !== null && o.id)
        .map((o) => ({
          id: String(o.id),
          apn: String(o.apn ?? ''),
          owners: Array.isArray(o.owners)
            ? (o.owners as unknown[]).map((name) => String(name))
            : [],
          landUse: String(o.landUse ?? ''),
          parcelAddress: String(o.parcelAddress ?? ''),
        }));
    }, [value.options]);

    const searchParams = useMemo(() => {
      const rawParams =
        typeof value.search_params === 'object' && value.search_params !== null
          ? (value.search_params as Record<string, unknown>)
          : {};
      return {
        address: String(rawParams.address ?? ''),
        city: String(rawParams.city ?? ''),
        state: String(rawParams.state ?? ''),
        zip_code: String(rawParams.zip_code ?? ''),
      };
    }, [value.search_params]);

    const handleOptionClick = (option: {
      id: string;
      parcelAddress: string;
    }) => {
      if (!option.id) {
        return;
      }
      const displayText = `full property report for ${option.parcelAddress}`;
      sendMessage(`${displayText}. property_id: ${option.id}`, displayText);
    };

    return (
      <PropertyOptionsPreviewCard
        options={options}
        searchParams={searchParams}
        onOptionClick={handleOptionClick}
      />
    );
  }
);

export const deepSearchReportPreviewCatalog = new Catalog(
  DEEP_SEARCH_CATALOG_ID,
  [
    AncestryReportPreview,
    AncestrySearchPreview,
    ContactSearchPreview,
    ObituaryReportPreview,
    ObituarySearchPreview,
    PersonReportPreview,
    PersonSearchPreview,
    PhoneReportPreview,
    EmailReportPreview,
    VehicleYmmReportPreview,
    VehicleVinReportPreview,
    VehicleLpReportPreview,
    UsernameReportPreview,
    PropertyReportPreview,
    PropertyUnitSelector,
  ]
);

export const DEEP_SEARCH_SUGGESTED_NEXT_STEPS_CATALOG_ID =
  'deepsearch-suggested-next-steps';

const SuggestedNextSteps = createBinderlessComponentImplementation(
  {
    name: 'SuggestedNextSteps',
    schema: z.object({}).strict(),
  },
  ({ context }) => {
    const value = context.dataContext.dataModel.get('/') as
      | Record<string, unknown>
      | undefined;

    if (!value) {
      return null;
    }

    const raw = Array.isArray(value.steps) ? value.steps : [];
    const data: SuggestedNextStepsData = {
      steps: raw.map((s) => String(s)),
    };

    return <SuggestedNextStepsCard data={data} />;
  }
);

export const deepSearchSuggestedNextStepsCatalog = new Catalog(
  DEEP_SEARCH_SUGGESTED_NEXT_STEPS_CATALOG_ID,
  [SuggestedNextSteps]
);

function normalizeAncestryRelatives(raw: unknown): AncestryTeaserRelative[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter(
      (r: unknown): r is Record<string, unknown> =>
        typeof r === 'object' && r !== null
    )
    .map((r) => ({
      name: String(r.name ?? ''),
      type: String(r.type ?? ''),
      fsId: String(r.fsId ?? ''),
    }));
}

function normalizeRelatives(raw: unknown): PersonTeaserRelative[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter(
      (r: unknown): r is Record<string, unknown> =>
        typeof r === 'object' && r !== null
    )
    .map((r) => ({
      name: String(r.name ?? ''),
      type: String(r.type ?? ''),
      bvId: String(r.bvId ?? ''),
    }));
}
